import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { useCatalogQuery } from "@/hooks/useCatalog";
import { CatalogProvider } from "@/providers/CatalogProvider";
import { useAuth } from "@/providers/AuthProvider";
import { LoadingScreen } from "@/components/feedback/LoadingScreen";
import { ErrorState } from "@/components/feedback/ErrorState";
import { TopBar } from "@/components/layout/TopBar";

export function AppLayout(): JSX.Element {
  const location = useLocation();
  const { user, isAuthLoading, logout } = useAuth();
  const catalogQuery = useCatalogQuery();

  const handleRetry = useCallback(() => {
    void catalogQuery.refetch();
  }, [catalogQuery]);

  if (isAuthLoading) {
    return <LoadingScreen message="正在恢复登录状态…" />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (catalogQuery.isLoading) {
    return <LoadingScreen message="正在同步量表与活动数据…" />;
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <ErrorState
        description="应用无法加载基础量表数据，请刷新或稍后重试。"
        onRetry={handleRetry}
      />
    );
  }

  return (
    <CatalogProvider value={catalogQuery.data}>
      <div
        data-app-shell
        className="flex h-screen min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_8%_0%,rgba(232,117,50,0.08),transparent_28rem),radial-gradient(circle_at_92%_10%,rgba(109,96,152,0.07),transparent_30rem),linear-gradient(135deg,#fbfcfa_0%,#f8f8f2_48%,#faf8fb_100%)] text-slate-900"
      >
        <a
          href="#main-content"
          className="sr-only z-50 rounded-lg bg-brand px-4 py-3 font-semibold text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:outline-none focus:ring-2 focus:ring-brand-dark focus:ring-offset-2"
        >
          跳到主要内容
        </a>
        <TopBar currentUser={user} onLogout={logout} />
        <main
          id="main-content"
          data-app-main
          className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </CatalogProvider>
  );
}
